import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, Bookmark, BookmarkCheck, Send, Shield, Clock, TrendingUp, Banknote, LogOut, FileCheck, AlertTriangle, CheckCircle2, X, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  'Open': 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30',
  'Partially Subscribed': 'bg-amber-500/15 text-amber-400 border border-amber-500/30',
  'Closing Soon': 'bg-red-500/15 text-red-400 border border-red-500/30',
  'Closed': 'bg-[#555]/20 text-[#999] border border-[#555]/30',
};

const NDA_TERMS = `NON-DISCLOSURE AGREEMENT

This Non-Disclosure Agreement ("Agreement") is entered into by the undersigned investor ("Recipient") and Sàwai Capital ("Discloser") regarding confidential deal information.

1. CONFIDENTIAL INFORMATION
All deal-related information, including but not limited to: Information Memoranda, financial projections, deal structures, originator details, term sheets, and any supplementary documentation shared through the Sàwai Capital Dealroom platform.

2. OBLIGATIONS
The Recipient agrees to:
a) Maintain strict confidentiality of all disclosed information.
b) Not disclose, reproduce, or distribute any confidential information to third parties without prior written consent from Sàwai Capital.
c) Use the information solely for the purpose of evaluating the investment opportunity.
d) Return or destroy all confidential materials upon request.

3. EXCLUSIONS
This Agreement does not apply to information that:
a) Is or becomes publicly available through no fault of the Recipient.
b) Was known to the Recipient prior to disclosure.
c) Is independently developed by the Recipient.
d) Is required to be disclosed by law or regulatory authority.

4. TERM
This Agreement remains in effect for a period of 24 months from the date of execution, or until the deal is publicly closed, whichever is later.

5. REMEDIES
The Recipient acknowledges that any breach may cause irreparable harm, and Sàwai Capital shall be entitled to seek injunctive relief in addition to any other remedies available at law.

6. GOVERNING LAW
This Agreement shall be governed by the laws of India, with exclusive jurisdiction in the courts of Mumbai, Maharashtra.

By accepting this agreement, you confirm that you have read, understood, and agree to be bound by the terms above.`;

function NDAModal({ deal, onAccept, onClose, loading }) {
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 30) {
      setScrolledToBottom(true);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="nda-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-[#1A1A1A] border border-[#A0743A]/40 max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-[#A0743A]/20 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <FileCheck size={20} className="text-[#A0743A]" strokeWidth={1.25} />
              <p className="text-[#C9A96E] text-xs uppercase tracking-[0.2em] font-semibold">Confidentiality Agreement Required</p>
            </div>
            <h2 className="text-2xl text-[#FAF8F5]" style={{ fontFamily: "'Cormorant Garamond', Cambria, Georgia, serif" }}>
              Non-Disclosure Agreement
            </h2>
            <p className="text-[#999] text-sm mt-1">For: <span className="text-[#FAF8F5]">{deal.deal_name}</span></p>
          </div>
          <button
            onClick={onClose}
            className="text-[#999] hover:text-[#FAF8F5] transition-colors p-1"
            data-testid="nda-close-btn"
          >
            <X size={20} />
          </button>
        </div>

        {/* NDA Content - Scrollable */}
        <div
          className="flex-1 overflow-y-auto p-6"
          onScroll={handleScroll}
          data-testid="nda-content-scroll"
        >
          <div className="bg-[#FAF8F5] p-6 text-[#1A1A1A] text-sm leading-relaxed whitespace-pre-line" style={{ fontFamily: "Calibri, 'Segoe UI', Arial, sans-serif" }}>
            {NDA_TERMS}
          </div>

          {!scrolledToBottom && (
            <div className="flex items-center gap-2 justify-center mt-4 text-[#C9A96E] text-xs">
              <AlertTriangle size={14} />
              <span>Please scroll to read the complete agreement</span>
            </div>
          )}
        </div>

        {/* Footer - Accept */}
        <div className="p-6 border-t border-[#A0743A]/20 space-y-4">
          <label className="flex items-start gap-3 cursor-pointer group" data-testid="nda-agree-checkbox-label">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              disabled={!scrolledToBottom}
              className="mt-1 w-4 h-4 accent-[#A0743A] cursor-pointer"
              data-testid="nda-agree-checkbox"
            />
            <span className={`text-sm leading-relaxed ${scrolledToBottom ? 'text-[#FAF8F5]' : 'text-[#555]'}`}>
              I have read, understood, and agree to the terms of this Non-Disclosure Agreement. I acknowledge that all deal information is strictly confidential.
            </span>
          </label>

          <div className="flex gap-3">
            <button
              data-testid="nda-accept-btn"
              onClick={onAccept}
              disabled={!agreed || loading}
              className="flex-1 bg-[#A0743A] text-[#FAF8F5] px-6 py-3 text-sm uppercase tracking-wider font-semibold hover:bg-[#C9A96E] transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <><div className="w-4 h-4 border-2 border-[#FAF8F5] border-t-transparent rounded-full animate-spin" /> Signing...</>
              ) : (
                <><FileCheck size={14} /> Accept &amp; Sign NDA</>
              )}
            </button>
            <button
              onClick={onClose}
              className="border border-[#A0743A]/40 text-[#A0743A] px-6 py-3 text-sm uppercase tracking-wider hover:bg-[#A0743A]/10 transition-colors"
              data-testid="nda-decline-btn"
            >
              Decline
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


export default function DealDetail() {
  const { dealId } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isWatched, setIsWatched] = useState(false);

  // NDA state
  const [ndaSigned, setNdaSigned] = useState(false);
  const [ndaData, setNdaData] = useState(null);
  const [ndaCheckLoading, setNdaCheckLoading] = useState(true);
  const [showNDAModal, setShowNDAModal] = useState(false);
  const [ndaSignLoading, setNdaSignLoading] = useState(false);

  // IM state
  const [imMessage, setImMessage] = useState('');
  const [imSubmitted, setImSubmitted] = useState(false);
  const [imLoading, setImLoading] = useState(false);

  useEffect(() => {
    const fetchDeal = async () => {
      try {
        const res = await fetch(`${API}/deals/${dealId}`, { credentials: 'include' });
        if (res.ok) setDeal(await res.json());
        else navigate('/portal');
      } catch { navigate('/portal'); }
      setLoading(false);
    };

    const checkWatchlist = async () => {
      try {
        const res = await fetch(`${API}/watchlist`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setIsWatched(data.watchlist_items.some(i => i.deal_id === dealId));
        }
      } catch {}
    };

    const checkNDA = async () => {
      try {
        const res = await fetch(`${API}/ndas/check/${dealId}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setNdaSigned(data.signed);
          setNdaData(data.nda);
        }
      } catch {}
      setNdaCheckLoading(false);
    };

    fetchDeal();
    checkWatchlist();
    checkNDA();
  }, [dealId, navigate]);

  const toggleWatchlist = async () => {
    if (isWatched) {
      await fetch(`${API}/watchlist/${dealId}`, { method: 'DELETE', credentials: 'include' });
      setIsWatched(false);
    } else {
      await fetch(`${API}/watchlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ deal_id: dealId }),
      });
      setIsWatched(true);
    }
  };

  const handleSignNDA = async () => {
    setNdaSignLoading(true);
    try {
      const res = await fetch(`${API}/ndas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ deal_id: dealId }),
      });
      if (res.ok) {
        const data = await res.json();
        setNdaSigned(true);
        setNdaData(data);
        setShowNDAModal(false);
      }
    } catch {}
    setNdaSignLoading(false);
  };

  const handleIMRequest = async (e) => {
    e.preventDefault();

    // Double-check NDA is signed
    if (!ndaSigned) {
      setShowNDAModal(true);
      return;
    }

    setImLoading(true);
    try {
      const res = await fetch(`${API}/im-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ deal_id: dealId, message: imMessage }),
      });
      if (res.ok) setImSubmitted(true);
    } catch {}
    setImLoading(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#A0743A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!deal) return null;

  return (
    <div className="min-h-screen bg-[#1A1A1A]" data-testid="deal-detail-page">
      {/* NDA Modal */}
      {showNDAModal && (
        <NDAModal
          deal={deal}
          onAccept={handleSignNDA}
          onClose={() => setShowNDAModal(false)}
          loading={ndaSignLoading}
        />
      )}

      {/* Header */}
      <header className="border-b border-[#A0743A]/20 bg-[#1A1A1A]/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/" className="text-[#A0743A] text-lg tracking-[0.15em]" style={{ fontFamily: "'Cormorant Garamond', Cambria, Georgia, serif" }}>
            SÀWAI CAPITAL
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/portal" className="text-[#FAF8F5] hover:text-[#A0743A] transition-colors text-xs uppercase tracking-wider" data-testid="nav-deals">Deals</Link>
            <Link to="/portal/watchlist" className="text-[#FAF8F5] hover:text-[#A0743A] transition-colors text-xs uppercase tracking-wider flex items-center gap-2" data-testid="nav-watchlist">
              <Bookmark size={14} /> Watchlist
            </Link>
            {['super_admin','admin','deal_manager','content_manager','investor_relations','view_only'].includes(user?.role) && (
              <Link to="/admin" className="text-[#C9A96E] hover:text-[#A0743A] transition-colors text-xs uppercase tracking-wider" data-testid="nav-admin">Admin</Link>
            )}
            <div className="flex items-center gap-3 pl-6 border-l border-[#A0743A]/20">
              <span className="text-[#999] text-xs hidden sm:block">{user?.name}</span>
              <button onClick={handleLogout} className="text-[#999] hover:text-[#FAF8F5] transition-colors" data-testid="logout-btn"><LogOut size={16} /></button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-10">
        {/* Back */}
        <Link to="/portal" className="flex items-center gap-2 text-[#A0743A] text-sm hover:text-[#C9A96E] transition-colors mb-8" data-testid="back-to-deals">
          <ArrowLeft size={16} /> Back to Deals
        </Link>

        {/* Deal Header */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <span className="bg-[#A0743A] text-[#FAF8F5] px-4 py-1.5 text-xs uppercase tracking-wider font-semibold">{deal.asset_class}</span>
              <span className={`px-3 py-1.5 text-xs uppercase tracking-wider font-semibold ${statusColors[deal.deal_status] || ''}`}>{deal.deal_status}</span>
              {/* NDA Status Badge */}
              {!ndaCheckLoading && (
                ndaSigned ? (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" data-testid="nda-signed-badge">
                    <CheckCircle2 size={12} /> NDA Signed
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider font-semibold bg-amber-500/15 text-amber-400 border border-amber-500/30" data-testid="nda-pending-badge">
                    <FileCheck size={12} /> NDA Required
                  </span>
                )
              )}
            </div>
            <h1 className="text-3xl md:text-4xl text-[#FAF8F5] mb-2">{deal.deal_name}</h1>
            {deal.deal_code && <p className="text-[#999] text-sm">{deal.deal_code}</p>}
          </div>
          <button
            data-testid="watchlist-toggle"
            onClick={toggleWatchlist}
            className={`flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-wider font-semibold transition-colors ${
              isWatched ? 'bg-[#A0743A] text-[#FAF8F5]' : 'border border-[#A0743A] text-[#A0743A] hover:bg-[#A0743A]/10'
            }`}
          >
            {isWatched ? <BookmarkCheck size={14} /> : <Bookmark size={14} />}
            {isWatched ? 'Watchlisted' : 'Add to Watchlist'}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: TrendingUp, label: 'Target Return', value: deal.target_return },
                { icon: Banknote, label: 'Deal Size', value: deal.deal_size },
                { icon: Clock, label: 'Tenor', value: deal.tenor },
                { icon: Shield, label: 'Sawai Rating', value: deal.sawai_rating },
              ].map(m => (
                <div key={m.label} className="border border-[#A0743A]/20 p-4">
                  <m.icon size={18} className="text-[#A0743A] mb-2" strokeWidth={1.25} />
                  <p className="text-[#999] text-[10px] uppercase tracking-wider mb-1">{m.label}</p>
                  <p className="text-[#FAF8F5] font-semibold text-sm">{m.value}</p>
                </div>
              ))}
            </div>

            {/* Description */}
            {deal.description && (
              <div className="border border-[#A0743A]/20 p-8">
                <h3 className="text-xl text-[#A0743A] mb-4" style={{ fontFamily: "'Cormorant Garamond', Cambria, Georgia, serif" }}>Deal Overview</h3>
                <p className="text-[#FAF8F5]/80 leading-relaxed">{deal.description}</p>
              </div>
            )}

            {/* Details Grid */}
            <div className="border border-[#A0743A]/20 p-8">
              <h3 className="text-xl text-[#A0743A] mb-6" style={{ fontFamily: "'Cormorant Garamond', Cambria, Georgia, serif" }}>Deal Structure</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { label: 'Deal Type', value: deal.deal_type },
                  { label: 'Min. Ticket Size', value: deal.min_ticket_size || 'On request' },
                  { label: 'Sector', value: deal.sector },
                  { label: 'Geography', value: deal.geography || 'Pan-India' },
                  { label: 'IM Status', value: deal.im_available },
                  { label: 'Security / Structure', value: deal.security_structure || 'Details in IM' },
                ].map(d => (
                  <div key={d.label} className="border-b border-[#A0743A]/10 pb-3">
                    <p className="text-[#999] text-xs uppercase tracking-wider mb-1">{d.label}</p>
                    <p className="text-[#FAF8F5]">{d.value}</p>
                  </div>
                ))}
              </div>
              {deal.originator_credentials && (
                <div className="mt-6 pt-6 border-t border-[#A0743A]/10">
                  <p className="text-[#999] text-xs uppercase tracking-wider mb-2">Originator Credentials</p>
                  <p className="text-[#FAF8F5]/80 text-sm leading-relaxed">{deal.originator_credentials}</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* NDA + IM Request Card */}
            <div className="bg-[#FAF8F5] border-l-4 border-[#A0743A] p-8">
              {imSubmitted ? (
                /* IM Request Success */
                <div data-testid="im-request-success">
                  <CheckCircle2 size={32} className="text-emerald-600 mb-4" strokeWidth={1.25} />
                  <h3 className="text-xl text-[#1A1A1A] mb-2" style={{ fontFamily: "'Cormorant Garamond', Cambria, Georgia, serif" }}>
                    IM Request Submitted
                  </h3>
                  <p className="text-[#555] text-sm">Our team will review your request and share the Information Memorandum within 1-2 business days.</p>
                  {ndaData && (
                    <div className="mt-4 pt-4 border-t border-[#1A1A1A]/10">
                      <p className="text-[#999] text-xs">NDA signed: {new Date(ndaData.signed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                    </div>
                  )}
                </div>
              ) : !ndaSigned ? (
                /* NDA Not Signed - Show NDA Prompt */
                <div data-testid="nda-required-prompt">
                  <FileCheck size={28} className="text-[#A0743A] mb-4" strokeWidth={1.25} />
                  <h3 className="text-xl text-[#1A1A1A] mb-3" style={{ fontFamily: "'Cormorant Garamond', Cambria, Georgia, serif" }}>
                    NDA Required
                  </h3>
                  <p className="text-[#555] text-sm mb-4 leading-relaxed">
                    To access the Information Memorandum for this deal, you must first review and sign a Non-Disclosure Agreement. This ensures all deal information remains confidential.
                  </p>
                  <div className="bg-[#1A1A1A]/5 p-4 mb-6">
                    <p className="text-[#1A1A1A] text-xs font-semibold uppercase tracking-wider mb-2">What you'll agree to:</p>
                    <ul className="text-[#555] text-sm space-y-1.5">
                      <li className="flex items-start gap-2"><span className="text-[#A0743A] mt-0.5">&#8226;</span> Maintain strict confidentiality of deal information</li>
                      <li className="flex items-start gap-2"><span className="text-[#A0743A] mt-0.5">&#8226;</span> Use information solely for investment evaluation</li>
                      <li className="flex items-start gap-2"><span className="text-[#A0743A] mt-0.5">&#8226;</span> Not disclose to unauthorized third parties</li>
                      <li className="flex items-start gap-2"><span className="text-[#A0743A] mt-0.5">&#8226;</span> Agreement valid for 24 months</li>
                    </ul>
                  </div>
                  <button
                    data-testid="sign-nda-btn"
                    onClick={() => setShowNDAModal(true)}
                    className="w-full bg-[#A0743A] text-[#FAF8F5] px-6 py-3 text-sm uppercase tracking-wider font-semibold hover:bg-[#C9A96E] transition-colors flex items-center justify-center gap-2"
                  >
                    <FileCheck size={14} /> Review &amp; Sign NDA
                  </button>
                </div>
              ) : (
                /* NDA Signed - Show IM Request Form */
                <div data-testid="im-request-form-section">
                  {/* NDA Confirmation */}
                  <div className="flex items-center gap-2 mb-4 pb-4 border-b border-[#1A1A1A]/10">
                    <CheckCircle2 size={16} className="text-emerald-600" />
                    <span className="text-emerald-700 text-xs font-semibold uppercase tracking-wider">NDA Signed</span>
                    {ndaData && (
                      <span className="text-[#999] text-xs ml-auto">
                        {new Date(ndaData.signed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    )}
                  </div>
                  {ndaData && (
                    <button
                      data-testid="download-nda-pdf-btn"
                      onClick={() => window.open(`${API}/ndas/${ndaData.nda_id}/pdf`, '_blank')}
                      className="w-full flex items-center justify-center gap-2 border border-[#A0743A]/30 text-[#A0743A] px-4 py-2 mb-5 text-xs uppercase tracking-wider hover:bg-[#A0743A]/10 transition-colors"
                    >
                      <Download size={13} /> Download Signed NDA (PDF)
                    </button>
                  )}

                  <h3 className="text-xl text-[#1A1A1A] mb-3" style={{ fontFamily: "'Cormorant Garamond', Cambria, Georgia, serif" }}>
                    Request Information Memorandum
                  </h3>
                  <form onSubmit={handleIMRequest}>
                    <p className="text-[#555] text-sm mb-4">
                      Express your interest in this deal. Our team will share the IM for your review.
                    </p>
                    <textarea
                      data-testid="im-message-input"
                      value={imMessage}
                      onChange={e => setImMessage(e.target.value)}
                      rows={4}
                      className="w-full border border-[#A0743A]/30 bg-white text-[#1A1A1A] p-3 mb-4 focus:border-[#A0743A] focus:outline-none resize-none text-sm"
                      placeholder="Optional: Add a note about your investment interest..."
                    />
                    <button
                      data-testid="request-im-btn"
                      type="submit"
                      disabled={imLoading}
                      className="w-full bg-[#A0743A] text-[#FAF8F5] px-6 py-3 text-sm uppercase tracking-wider font-semibold hover:bg-[#C9A96E] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {imLoading ? 'Sending...' : 'Request IM'} <Send size={14} />
                    </button>
                  </form>
                </div>
              )}
            </div>

            {/* Contact */}
            <div className="border border-[#A0743A]/20 p-6">
              <p className="text-[#999] text-xs uppercase tracking-wider mb-3">Contact</p>
              <p className="text-[#FAF8F5] text-sm mb-1">Sàwai Capital Dealroom</p>
              <a href="mailto:dealroom@sawaicap.com" className="text-[#A0743A] text-sm hover:text-[#C9A96E] transition-colors">
                dealroom@sawaicap.com
              </a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
